import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import LustLinkSeal from '../common/LustLinkSeal';
import { 
  House, 
  Compass, 
  Heart,
  User,
  Coins,
  Gear,
  SignOut,
  Sparkle,
  ShieldCheck,
  Warning,
  FilmStrip,
  Crown,
  Broadcast,
  Upload,
  ChartBar,
  ShareNetwork,
  PaintBrush,
  Storefront,
  ChatCenteredDots,
  Translate,
  DotsThree,
  X,
  Calendar,
  Wallet,
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage, useTranslation } from '../../context/LanguageContext';

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  // Hide Emergent badge when mobile More menu is open
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = moreOpen ? 'none' : '';
  }, [moreOpen]);

  const navItems = [
    { path: '/feed', icon: House, label: t('nav_feed') },
    { path: '/browse', icon: FilmStrip, label: t('nav_browse') },
    { path: '/explore', icon: Compass, label: 'Explore' },
    { path: '/store', icon: Storefront, label: 'The Vault' },
    { path: '/live', icon: Broadcast, label: t('nav_live') },
    { path: '/tokens', icon: Coins, label: t('nav_tokens') },
    { path: '/orders', icon: Wallet, label: 'My Orders' },
    { path: '/feedback', icon: ChatCenteredDots, label: 'Feedback' },
    { path: '/profile', icon: User, label: t('nav_profile') },
  ];

  // Add creator dashboard for creators
  if (user?.role === 'creator') {
    navItems.splice(5, 0, { path: '/creator-dashboard', icon: ChartBar, label: 'Dashboard' });
    navItems.splice(6, 0, { path: '/referrals', icon: ShareNetwork, label: t('nav_referrals') });
  }

  const isActive = (path) => {
    if (path === '/profile') {
      return location.pathname === '/profile' || location.pathname.startsWith('/profile/');
    }
    if (path === '/verification') {
      return location.pathname === '/verification';
    }
    return location.pathname.startsWith(path);
  };

  const isCreator = user?.role === 'creator';
  const isCreatorVerified = user?.is_creator_verified;
  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-[280px] border-r border-white/10 bg-slate-950/50 backdrop-blur-md flex-col z-40 overflow-y-auto overflow-x-hidden">
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <LustLinkSeal size={40} showArcText={false} />
            <span className="font-heading text-xl font-bold text-white">LustLink</span>
          </div>
        </div>

        {/* Creator Verification Banner */}
        {isCreator && !isCreatorVerified && (
          <NavLink
            to="/verification"
            className="mx-4 mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3 hover:bg-amber-500/20 transition-colors"
            data-testid="verification-banner"
          >
            <Warning weight="fill" className="w-5 h-5 text-amber-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-400">Verify Identity</p>
              <p className="text-xs text-amber-400/70">Required to post content</p>
            </div>
          </NavLink>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive(item.path)
                  ? 'bg-blue-500/10 text-blue-500'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-6 h-6" />
              <span className="font-medium">{item.label}</span>
              {item.badge && (
                <span className={`ml-auto min-w-[20px] h-5 flex items-center justify-center px-1.5 text-xs font-bold rounded-full ${
                  item.badgeColor === 'rose' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-slate-700 text-slate-300'
                }`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </NavLink>
          ))}
          
          {/* Creator Verification Link */}
          {isCreator && (
            <NavLink
              to="/verification"
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive('/verification')
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid="nav-verification"
            >
              <ShieldCheck weight={isActive('/verification') ? 'duotone' : 'regular'} className="w-6 h-6" />
              <span className="font-medium">Verification</span>
              {isCreatorVerified && (
                <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                  Verified
                </span>
              )}
            </NavLink>
          )}
          
          {/* Admin Link */}
          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-white/5">
              <p className="px-4 text-xs font-bold uppercase tracking-wider text-amber-500 mb-2">Admin</p>
              <NavLink
                to="/admin"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive('/admin')
                    ? 'bg-amber-500/10 text-amber-400'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid="nav-admin"
              >
                <Crown weight={isActive('/admin') ? 'duotone' : 'regular'} className="w-6 h-6" />
                <span className="font-medium">Admin Panel</span>
              </NavLink>
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-slate-800 overflow-hidden">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <User className="w-5 h-5" />
                  </div>
                )}
              </div>
              {isCreator && isCreatorVerified && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-slate-950">
                  <ShieldCheck weight="fill" className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.display_name}</p>
              <p className="text-sm text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <NavLink
              to="/settings"
              className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              data-testid="nav-settings"
            >
              <Gear className="w-5 h-5" />
            </NavLink>
            <button
              onClick={toggleLanguage}
              className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors flex items-center gap-1"
              data-testid="lang-toggle-btn"
              title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
            >
              <Translate className="w-5 h-5" />
              <span className="text-xs font-bold uppercase">{language}</span>
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 rounded-full">
              <Coins weight="duotone" className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-400">{user?.tokens || 0}</span>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2.5 text-slate-400 hover:text-blue-400 bg-slate-800/50 hover:bg-blue-500/10 rounded-lg transition-colors"
            data-testid="logout-btn"
            title="Sign Out"
          >
            <SignOut className="w-5 h-5" />
            <span className="text-sm font-medium">Logout</span>
          </button>
          <span className="text-[10px] text-slate-700 text-center mt-1" data-testid="app-version">v4.8.4</span>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-[280px] pb-20 md:pb-0 min-h-screen overflow-x-auto">
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation — 5 key items + More menu */}
      {(() => {
        const mobileMainItems = [
          { path: '/feed', icon: House, label: 'Feed' },
          { path: '/browse', icon: FilmStrip, label: 'Browse' },
          { path: '/live', icon: Broadcast, label: 'Live' },
          { path: '/explore', icon: Compass, label: 'Explore' },
        ];
        const mobileMoreItems = navItems.filter(
          item => !mobileMainItems.some(m => m.path === item.path)
        );
        return (
          <>
            {/* More Menu Overlay */}
            {moreOpen && (
              <div className="md:hidden fixed inset-0 z-[100]" data-testid="more-menu-overlay">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
                <div className="absolute bottom-16 left-0 right-0 bg-slate-900 border-t border-white/10 rounded-t-2xl p-4 pb-6 animate-in slide-in-from-bottom duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white font-semibold text-sm">Menu</span>
                    <button onClick={() => setMoreOpen(false)} className="p-1 text-slate-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {mobileMoreItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => { navigate(item.path); setMoreOpen(false); }}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors ${
                          isActive(item.path) ? 'bg-blue-500/10 text-blue-400' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`more-nav-${item.label.toLowerCase()}`}
                      >
                        <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-6 h-6" />
                        <span className="text-[10px] font-medium">{item.label}</span>
                      </button>
                    ))}
                    {isAdmin && (
                      <button
                        onClick={() => { navigate('/admin'); setMoreOpen(false); }}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors ${
                          isActive('/admin') ? 'bg-amber-500/10 text-amber-400' : 'text-slate-400 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid="more-nav-admin"
                      >
                        <Crown weight={isActive('/admin') ? 'duotone' : 'regular'} className="w-6 h-6" />
                        <span className="text-[10px] font-medium">Admin</span>
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => { setMoreOpen(false); logout(); }}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800 hover:bg-blue-500/20 text-slate-300 hover:text-blue-400 rounded-xl transition-colors"
                    data-testid="mobile-logout-btn"
                  >
                    <SignOut className="w-5 h-5" />
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>
            )}

            <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 border-t border-white/10 bg-slate-950/95 backdrop-blur-2xl px-2 flex justify-around items-center z-[90]">
              {mobileMainItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`relative flex flex-col items-center gap-1 px-3 py-2 ${
                    isActive(item.path) ? 'text-blue-500' : 'text-slate-400'
                  }`}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  <div className="relative">
                    <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-6 h-6" />
                    {item.badge && (
                      <span className={`absolute -top-1.5 -right-2.5 min-w-[16px] h-4 flex items-center justify-center px-1 text-[10px] font-bold rounded-full ${
                        item.badgeColor === 'rose' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-slate-600 text-white'
                      }`}>
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-medium">{item.label}</span>
                </NavLink>
              ))}
              <button
                onClick={() => setMoreOpen(prev => !prev)}
                className={`relative flex flex-col items-center gap-1 px-3 py-2 ${moreOpen ? 'text-blue-500' : 'text-slate-400'}`}
                data-testid="mobile-more-btn"
              >
                <DotsThree weight="bold" className="w-6 h-6" />
                <span className="text-[10px] font-medium">More</span>
              </button>
            </nav>
          </>
        );
      })()}
    </div>
  );
};

export default MainLayout;
